"use client";

import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";

import { TextField } from "@/components/shared/text-field";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup } from "@/components/ui/field";
import { verifyMfaAction } from "@/features/auth/actions";
import { FormAlert } from "@/features/auth/components/form-alert";
import { FormHeader } from "@/features/auth/components/form-header";
import { applyServerErrors } from "@/features/auth/form-errors";
import { mfaCodeFormSchema, type MfaCodeFormInput } from "@/features/auth/schemas";

/**
 * The second sign-in step (membership-and-mfa.md rule 16): an authenticator code, or a
 * recovery code. `onRestart` goes back to the password step, with the reason if any.
 */
export function MfaCodeForm({
  challengeToken,
  next,
  onRestart,
}: {
  challengeToken: string;
  next: string | null;
  onRestart: (message?: string) => void;
}) {
  const t = useTranslations("auth.mfa");
  const [recovery, setRecovery] = useState(false);
  const form = useForm<MfaCodeFormInput>({
    resolver: zodResolver(mfaCodeFormSchema),
    defaultValues: { code: "" },
  });

  async function handleSubmit(values: MfaCodeFormInput) {
    const result = await verifyMfaAction({ challengeToken, code: values.code }, next);
    if (result.ok) return; // redirected
    if (result.fieldErrors?.challengeToken) return onRestart(result.error);
    applyServerErrors(form, ["code"], result.error, result.fieldErrors);
  }

  const { isSubmitting, errors } = form.formState;

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} noValidate className="flex flex-col gap-6">
      <FieldGroup>
        <FormHeader
          title={t("codeTitle")}
          description={t(recovery ? "recoveryDescription" : "codeDescription")}
        />
        <FormAlert message={errors.root?.message} />

        <TextField
          key={recovery ? "recovery" : "totp"}
          control={form.control}
          name="code"
          label={t(recovery ? "recoveryCode" : "code")}
          autoComplete={recovery ? "off" : "one-time-code"}
          inputMode={recovery ? "text" : "numeric"}
        />

        <Field>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("verifying") : t("verify")}
          </Button>
          <Button
            type="button"
            variant="link"
            onClick={() => {
              setRecovery(!recovery);
              form.reset({ code: "" });
            }}
          >
            {t(recovery ? "useAuthenticator" : "useRecoveryCode")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => onRestart()}>
            {t("back")}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
